import { useEffect, useMemo, useState } from "react";
import { ComposedChart, Area, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { CaretLeft, CaretRight, TrendUp, TrendDown, ChartLineUp } from "@phosphor-icons/react";
import api from "@/lib/api";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const money = (n, cur = "MXN") => `${Number(n || 0) < 0 ? "−" : ""}${cur === "USD" ? "$" : "MX$"}${Math.abs(Number(n || 0)).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const compact = (n) => (Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : Math.abs(n) >= 1e3 ? `${Math.round(n / 1e3)}k` : String(Math.round(n)));

function TooltipBox({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  if (d.total_mxn == null) return null;
  const net = d.inflow_mxn - d.outflow_mxn;
  return (
    <div className="bg-white border border-zinc-200 shadow-lg p-3 text-xs space-y-1 min-w-[190px]" data-testid="treasury-tooltip">
      <div className="font-mono-data tracking-widest text-zinc-500 uppercase">{MONTHS[d.month - 1]} · {d.closed ? "cierre de mes" : "hoy"}</div>
      <div className="font-heading text-lg font-extrabold text-[#002FA7] numeric">{money(d.total_mxn)}</div>
      <div className="flex justify-between text-zinc-600"><span>En pesos</span><b className="numeric">{money(d.mxn)}</b></div>
      <div className="flex justify-between text-zinc-600"><span>En dólares</span><b className="numeric">{money(d.usd, "USD")}</b></div>
      <div className="border-t border-zinc-100 pt-1 flex justify-between"><span className="text-zinc-500">Movimiento del mes</span><b className={`numeric ${net >= 0 ? "text-[#00873D]" : "text-[#E0000F]"}`}>{net >= 0 ? "+" : "−"}{money(Math.abs(net))}</b></div>
    </div>
  );
}

export default function TreasuryGrowthChart({ refreshKey }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/banks-history", { params: { year } })
      .then((r) => { if (alive) { setData(r.data); setError(false); } })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [year, refreshKey]);

  const rows = useMemo(() => (data?.months || []).map((m) => ({ ...m, label: MONTHS[m.month - 1], net: m.total_mxn == null ? null : m.inflow_mxn - m.outflow_mxn })), [data]);
  const known = rows.filter((r) => r.total_mxn != null);
  const first = known[0], last = known[known.length - 1];
  const growth = first && last && known.length > 1 ? last.total_mxn - first.total_mxn : null;
  const growthPct = growth != null && first.total_mxn > 0.01 ? (growth / first.total_mxn) * 100 : null;
  const avgNet = known.length ? known.reduce((s, r) => s + r.net, 0) / known.length : 0;
  const best = known.length ? known.reduce((a, b) => (b.net > a.net ? b : a)) : null;
  const years = data?.years?.length ? data.years : [year];
  const canPrev = year > years[0], canNext = year < new Date().getFullYear();

  return (
    <section className="border border-zinc-200 bg-white p-5" data-testid="treasury-growth">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500 inline-flex items-center gap-1.5"><ChartLineUp size={13} weight="bold" /> Crecimiento de tesorería</div>
          <div className="text-sm text-zinc-500 mt-0.5">Dinero total al cierre de cada mes (pesos + dólares al TC {Number(data?.fx || 0).toFixed(2)})</div>
        </div>
        <div className="inline-flex items-center border border-zinc-200 rounded-sm" data-testid="treasury-year-nav">
          <button type="button" disabled={!canPrev} onClick={() => setYear((y) => y - 1)} className="px-2 py-1.5 hover:bg-zinc-50 disabled:opacity-30" data-testid="treasury-prev-year"><CaretLeft size={14} weight="bold" /></button>
          <span className="font-mono-data font-bold text-sm px-3 tabular-nums" data-testid="treasury-year">{year}</span>
          <button type="button" disabled={!canNext} onClick={() => setYear((y) => y + 1)} className="px-2 py-1.5 hover:bg-zinc-50 disabled:opacity-30" data-testid="treasury-next-year"><CaretRight size={14} weight="bold" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <Stat label={`Cierre ${first ? MONTHS[first.month - 1] : "—"}`} value={first ? money(first.total_mxn) : "—"} testid="treasury-first" />
        <Stat label={`${last ? (last.closed ? "Cierre " + MONTHS[last.month - 1] : "Hoy") : "—"}`} value={last ? money(last.total_mxn) : "—"} accent testid="treasury-last" />
        <Stat
          label="Crecimiento del año"
          value={growth == null ? "—" : `${growth >= 0 ? "+" : "−"}${money(Math.abs(growth))}`}
          sub={growthPct == null ? null : `${growthPct >= 0 ? "+" : ""}${growthPct.toFixed(1)}%`}
          tone={growth == null ? null : growth >= 0 ? "up" : "down"}
          testid="treasury-growth-value"
        />
        <Stat label="Promedio mensual" value={known.length ? `${avgNet >= 0 ? "+" : "−"}${money(Math.abs(avgNet))}` : "—"} sub={best ? `Mejor mes: ${MONTHS[best.month - 1]}` : null} tone={known.length ? (avgNet >= 0 ? "up" : "down") : null} testid="treasury-avg" />
      </div>

      <div className="mt-4 h-[260px]" data-testid="treasury-chart">
        {loading && !data ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-zinc-400">Cargando…</div>
        ) : error && !data ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-[#E0000F]" data-testid="treasury-error">No se pudo cargar el historial. Intenta de nuevo.</div>
        ) : known.length === 0 ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-zinc-400">Sin movimientos en {year}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="treasuryFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#002FA7" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#002FA7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="bal" tickFormatter={compact} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={48} />
              <YAxis yAxisId="net" orientation="right" hide />
              <Tooltip content={<TooltipBox />} cursor={{ stroke: "#002FA7", strokeDasharray: "3 3" }} />
              <ReferenceLine yAxisId="net" y={0} stroke="#e4e4e7" />
              <Bar yAxisId="net" dataKey="net" radius={[3, 3, 0, 0]} barSize={14} fillOpacity={0.35}>
                {rows.map((r) => <Cell key={r.month} fill={(r.net ?? 0) < 0 ? "#E0000F" : "#00873D"} />)}
              </Bar>
              <Area yAxisId="bal" type="monotone" dataKey="total_mxn" stroke="#002FA7" strokeWidth={2.5} fill="url(#treasuryFill)" connectNulls={false} dot={{ r: 3, fill: "#002FA7", strokeWidth: 0 }} activeDot={{ r: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="mt-2 flex items-center gap-4 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#002FA7] inline-block rounded" /> Saldo al cierre del mes</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#00873D]/40 inline-block rounded-sm" /> Entró más de lo que salió</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#E0000F]/40 inline-block rounded-sm" /> Salió más de lo que entró</span>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, tone, accent, testid }) {
  const Icon = tone === "up" ? TrendUp : tone === "down" ? TrendDown : null;
  const color = tone === "up" ? "text-[#00873D]" : tone === "down" ? "text-[#E0000F]" : accent ? "text-[#002FA7]" : "text-zinc-900";
  return (
    <div className="border border-zinc-100 bg-zinc-50/60 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">{label}</div>
      <div className={`numeric text-lg font-extrabold mt-0.5 inline-flex items-center gap-1 ${color}`}>{Icon && <Icon size={16} weight="bold" />}{value}</div>
      {sub && <div className={`text-[11px] font-semibold ${color}`}>{sub}</div>}
    </div>
  );
}
